// Sprint 17 A4: Contact Timeline — chronological event list with Lucide icons
import {
  UserPlus, Pencil, Handshake, BarChart3, Trophy, XCircle,
  CheckCircle, ClipboardList, FileText, Mail, Eye, Link2,
  CreditCard, Calendar, Pin,
} from "lucide-react";
import { Skeleton, SkeletonAvatar } from "@/components/skeleton";
import type { TimelineEntry } from "@/hooks/use-contacts";
import { formatDate, formatTime } from "@/lib/i18n";

interface ContactTimelineProps {
  entries: TimelineEntry[];
  isLoading: boolean;
}

const EVENT_ICONS: Record<string, { Icon: typeof Mail; color: string }> = {
  "crm.person.created": { Icon: UserPlus, color: "text-[var(--color-primary)]" },
  "crm.person.updated": { Icon: Pencil, color: "text-[var(--color-muted-foreground)]" },
  "crm.deal.created": { Icon: Handshake, color: "text-[var(--color-primary)]" },
  "crm.deal.stage_changed": { Icon: BarChart3, color: "text-[var(--color-warning)]" },
  "crm.deal.won": { Icon: Trophy, color: "text-[var(--color-success)]" },
  "crm.deal.lost": { Icon: XCircle, color: "text-[var(--color-destructive)]" },
  "crm.task.completed": { Icon: CheckCircle, color: "text-[var(--color-success)]" },
  "crm.task.created": { Icon: ClipboardList, color: "text-[var(--color-primary)]" },
  "crm.activity.logged": { Icon: FileText, color: "text-[var(--color-muted-foreground)]" },
  "email.sent": { Icon: Mail, color: "text-[var(--color-primary)]" },
  "email.opened": { Icon: Eye, color: "text-[var(--color-info)]" },
  "email.clicked": { Icon: Link2, color: "text-[var(--color-accent-action)]" },
  "payment.completed": { Icon: CreditCard, color: "text-[var(--color-success)]" },
  "service.booking.completed": { Icon: Calendar, color: "text-[var(--color-accent-action)]" },
  ai_summary: { Icon: FileText, color: "text-[var(--color-ai)]" },
  booking: { Icon: Calendar, color: "text-[var(--color-accent-action)]" },
  call: { Icon: FileText, color: "text-[var(--color-ai)]" },
  deal: { Icon: Handshake, color: "text-[var(--color-warning)]" },
  email: { Icon: Mail, color: "text-[var(--color-primary)]" },
  form: { Icon: FileText, color: "text-[var(--color-info)]" },
  note: { Icon: FileText, color: "text-[var(--color-muted-foreground)]" },
  page: { Icon: Eye, color: "text-[var(--color-info)]" },
  system: { Icon: Pin, color: "text-[var(--color-muted-foreground)]" },
};

const DEFAULT_ICON = { Icon: Pin, color: "text-[var(--color-muted-foreground)]" };

/** Activity timeline for a contact — chronological event list. */
export function ContactTimeline({ entries, isLoading }: ContactTimelineProps) {
  if (isLoading) {
    return (
      <div className="space-y-3 py-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex gap-3">
            <SkeletonAvatar size="sm" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-3 w-1/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-[var(--color-muted-foreground)]">
        No activity yet
      </p>
    );
  }

  return (
    <div className="relative space-y-0 py-2">
      {/* Vertical line */}
      <div className="absolute left-4 top-0 bottom-0 w-px bg-[var(--color-border)]" />

      {entries.map((entry) => {
        const config = EVENT_ICONS[getTimelineIconKey(entry)] ?? DEFAULT_ICON;
        const Icon = config.Icon;
        return (
          <div key={entry.id} className="stagger-item relative flex gap-3 py-2 pl-1">
            <div className={`z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--color-card)] ring-2 ring-[var(--color-border)] ${config.color}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 pt-0.5">
              <p className="text-sm text-[var(--color-foreground)]">
                {formatEventDescription(entry)}
              </p>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                {formatTimestamp(entry.createdAt ?? entry.timestamp)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function getTimelineIconKey(entry: TimelineEntry): string {
  return entry.eventType ?? entry.type ?? "";
}

function formatEventDescription(entry: TimelineEntry): string {
  if (entry.title) {
    return entry.body ? `${entry.title}: ${entry.body}` : entry.title;
  }

  const type = entry.eventType ?? entry.type ?? "activity";
  const payload = entry.payload ?? entry.metadata ?? {};

  switch (type) {
    case "crm.person.created": return "Contact created";
    case "crm.person.updated": return "Contact updated";
    case "crm.deal.created": return `Deal created: ${payload.dealName ?? "Untitled"}`;
    case "crm.deal.stage_changed": return `Deal moved to ${payload.toStage ?? "new stage"}`;
    case "crm.deal.won": return "Deal won";
    case "crm.deal.lost": return `Deal lost${payload.lostReason ? `: ${payload.lostReason}` : ""}`;
    case "crm.task.completed": return `Task completed: ${payload.title ?? ""}`;
    case "crm.task.created": return `Task created: ${payload.title ?? ""}`;
    case "crm.activity.logged": return `Activity: ${payload.type ?? "note"}`;
    case "email.sent": return `Email sent: ${payload.subject ?? ""}`;
    case "email.opened": return "Email opened";
    case "email.clicked": return "Email link clicked";
    case "payment.completed": return `Payment received: ${payload.currency ?? ""} ${((payload.amountCents as number) / 100).toFixed(2)}`;
    case "service.booking.completed": return "Booking completed";
    default: return type.replace(/[._]/g, " ");
  }
}

function formatTimestamp(iso: string | undefined): string {
  if (!iso) return "Unknown time";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Unknown time";
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffDays === 0) {
    return `Today at ${formatTime(date)}`;
  }
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return formatDate(date);
}
